import { DateTime } from "luxon";
import {
	SBBox,
	SBButton,
	SBGrid,
	SBOutlinedButton,
	SBTypography,
	SBIcon,
	SBChip,
} from "modules/Commons";
import { ModifierOperationType } from "modules/Forecast/models/modifierOperationType";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import SBSystemSearch from "views/Commons/SBSystemSearch";
import ConfigTextField from "views/ConfigsView/ConfigSideMenu/ConfigTextField";
import ForecastModifierPeriod from "./ForecastModifierPeriod";
import { IDatePicker } from "domain/Utils/models/IDatePicker";
import ForecastModifierOperationType from "./ForecastModifierOperationType";
import { SBTranslate } from "i18n";

export default ({
	config,
	loading,
	onUpdate,
	onClose,
	fetchPropError,
}: any) => {
	const { getValues, setValue } = useFormContext();
	const [updateFilter, setUpdateFilter] = useState(false);

	const buildFilterItems = () =>
		!!config
			? [
				getValues("products")?.map((p: any) => ({
					...p,
					color: "primary",
					icon: "package",
					type: "product",
				})) ?? [],
				getValues("stores")?.map((s: any) => ({
					...s,
					color: "darkPurple",
					icon: "home",
					type: "store",
				})) ?? [],
				getValues("categories")?.map((c: any) => ({
					...c,
					color: "secondary",
					icon: "bookmark",
					type: "category",
				})) ?? [],
			]
			: [];

	const updateFilterItems = (newItems: any[]) => {
		setFilterItems(newItems);
		setValue(
			"products",
			newItems.filter((c) => c.type === "product")
		);
		setValue(
			"categories",
			newItems.filter((c) => c.type === "category")
		);
		setValue(
			"stores",
			newItems.filter((c) => c.type === "store")
		);
	};

	const onRemoveFromChip = (itemName: string) => {
		const newFilterItems = filterItems.filter((v) => v.name !== itemName);
		updateFilterItems(newFilterItems);
		setUpdateFilter((c) => !c);
	};

	const selectedFiltersChips = buildFilterItems();
	const [filterItems, setFilterItems] = useState(
		selectedFiltersChips.flatMap((i) => i)
	);

	const multiplierAdornment = <>x</>;
	const additiveAdornment = <>+</>;

	const [modifierStartDate, setModifierStartDate] = useState<DateTime | undefined>(
		getValues("startDate") ? DateTime.fromFormat(getValues("startDate"), "yyyy-MM-dd") : undefined
	);
	const [modifierEndDate, setModifierEndDate] = useState<DateTime | undefined>(
		getValues("endDate") ? DateTime.fromFormat(getValues("endDate"), "yyyy-MM-dd") : undefined
	);
	const [modifierOperationType, setModifierOperationType] = useState<ModifierOperationType | null>(
		getValues("operationType") ? getValues("operationType") : ""
	);
	const [modifier, setModifier] = useState<number | "">(
		getValues("modifier") ? getValues("modifier") : ""
	);

	const updateModifierStartDate = (value: DateTime | undefined) =>
		setModifierStartDate(value);
	const updateModifierEndDate = (value: DateTime | undefined) =>
		setModifierEndDate(value);
	const setConfigModifierOperationType = (v: ModifierOperationType | null) =>
		setModifierOperationType(v);
	const updateModifier = (value: string) =>
		setModifier(value.positiveNumber());

	const getModifierAdornmentType = () => {
		if (modifierOperationType == ModifierOperationType.Additive) return additiveAdornment;
		if (modifierOperationType == ModifierOperationType.Multiplicative) return multiplierAdornment;
	}

	useEffect(() => {
		setValue("startDate", modifierStartDate ? modifierStartDate?.toFormat("yyyy-MM-dd") : undefined);
		setValue("endDate", modifierEndDate ? modifierEndDate?.toFormat("yyyy-MM-dd") : undefined);
		setValue("operationType", modifierOperationType);
		setValue("modifier", modifier);
	}, [
		modifierStartDate,
		modifierEndDate,
		modifierOperationType,
		modifier
	]);

	return (
		<SBBox px={3}>
			<SBBox display="flex" alignItems="center" mt={2}>
				<SBTypography fontWeight="700">{SBTranslate("modifier")}</SBTypography>
				<SBBox ml="auto" mr={2}>
					<SBButton loading={loading} type="submit">
						<SBIcon icon="save" color="#fff" />
						<SBTypography fontWeight="700" ml={1}>
							{SBTranslate("save")}
						</SBTypography>
					</SBButton>
				</SBBox>
				<SBOutlinedButton disabled={loading} onClick={onClose}>
					<SBTypography py={0.3} fontWeight="700">
						{SBTranslate("close")}
					</SBTypography>
				</SBOutlinedButton>
			</SBBox>
			<SBGrid container>
				<SBBox display="flex" flexDirection="column" width={1} mt={4}>
					<SBTypography title="body" fontWeight="700" mb={2} color="gray2.main">
						{SBTranslate("nameThisModifier")}
					</SBTypography>
					<SBGrid alignItems="center">
						<SBGrid item xs={12}>
							<ConfigTextField
								section="filter"
								prop="name"
								placeholder={SBTranslate("name")}
								title={SBTranslate("name")}
								helperText={fetchPropError("name")}
								onClick={() => { }}
							/>
						</SBGrid>
					</SBGrid>
					<SBBox>
						<SBTypography title="body" fontWeight="700" mb={2} color="gray2.main">
							{SBTranslate("selectFilters")}
						</SBTypography>
						{(fetchPropError("") ||
							fetchPropError("products") ||
							fetchPropError("categories") ||
							fetchPropError("stores")) && (
								<SBTypography color="error" variant="caption" mb={1}>
									{SBTranslate("filterEmpty")}
								</SBTypography>
							)}
					</SBBox>
					<SBSystemSearch
						solo
						key={+updateFilter}
						filterIsolated="category"
						fullWidth
						onlyActives={false}
						loading={false}
						onChange={(items: any) => updateFilterItems(items)}
						onClick={() => { }}
						defaultItems={filterItems}
						filterType={filterItems[0]?.type}
						categoryDeep={filterItems[0]?.deep}
					/>
					<SBGrid container spacing={1} marginTop={2}>
						{selectedFiltersChips &&
							selectedFiltersChips.map(
								(list, index) =>
									list.length > 0 && (
										<SBGrid item xs={6} mt={1} key={index}>
											{list.map((item: any) => (
												<SBBox mb={1} key={item.name}>
													<SBChip
														icon={item.icon}
														color={item.color}
														label={`(${item.clientID}) ${item.name}`}
														onDelete={() =>
															onRemoveFromChip(
																item.name
															)
														}
													/>
												</SBBox>
											))}
										</SBGrid>
									)
							)}
					</SBGrid>
					<SBTypography title="body" fontWeight="700" mt={2} mb={2} color="gray2.main">
						{SBTranslate("forecastModifier")}
					</SBTypography>
					<SBGrid container spacing={4}>
						<SBGrid item md={12} lg={6} xl={4}>
							<ForecastModifierPeriod
								startDate={modifierStartDate}
								endDate={modifierEndDate}
								onChange={(e: IDatePicker) => {
									updateModifierStartDate(e.range?.startDate)
									updateModifierEndDate(e.range?.endDate)
								}}
							/>
						</SBGrid>
						<SBGrid item md={12} lg={6} xl={4}>
							<ForecastModifierOperationType
								value={modifierOperationType}
								onUpdate={setConfigModifierOperationType}
							/>
						</SBGrid>
						<SBGrid item md={12} lg={6} xl={4}>
							<ConfigTextField
								section="modifier"
								type="number"
								title={SBTranslate("modifier")}
								value={modifier}
								helperText={fetchPropError("modifier")}
								endAdornment={getModifierAdornmentType()}
								onChange={(e) => updateModifier(e.target.value)}
								onClick={() => { }}
							/>
						</SBGrid>
					</SBGrid>
				</SBBox>
			</SBGrid>
		</SBBox>
	);
};
